import { z } from "zod";
import { throwError, ZodSchema } from "@validation/utils/schemaUtils";
import { AgreementTypeEnum, CharterType, DeclarationMonthTerm } from "__generated__/enums";
import { CalculationMethodValidationSchema } from "view/calculationmethods/validation/calculationMethod.zod";
import { AgreementConditionValidationSchema } from "view/agreementconditions/validation/agreementcondition.zod";
import { AgreementRow, CalculationMethodRow, getRequiredAgreementFields } from "view/agreements/validation/requiredUtils";
import { getKeyFromPath, isCalculationMethodPath, isTopLevelPath, PathType } from "view/agreements/validation/pathUtils";

export type AgreementFormInput = z.input<typeof AgreementValidationSchema>;
export type AgreementFormOutput = z.output<typeof AgreementValidationSchema>;

export const AgreementValidationSchema = z.object({
	id: z.number().nullish(),
	uuid: z.string().nullish(),
	type: ZodSchema.requiredField(z.enum(AgreementTypeEnum)),
	summary: z.string().optional().transform(_ => undefined),
	costType: z.object({
		id: z.number().nullish(),
		title_en: z.string().nullish(),
		title_nl: z.string().nullish(),
		title_de: z.string().nullish(),
	}).nullish(),
	priority: z.number().nullish(),
	description: z.string().max(255).nullish(),
	manualActionRequired: z.boolean().nullish(),
	primary: z.boolean().nullish(),
	days: ZodSchema.optionalInt(0, 999999999),
	availableHoursADay: ZodSchema.optionalInt(12, 24),
	workdays: ZodSchema.optionalInt(1, 3),
	declarationMonthTerm: z.enum(DeclarationMonthTerm).nullish(),
	ships: z.array(z.object({
		id: z.number(),
		name: z.string(),
	})).nullish(),
	location: z.object({
		id: z.number(),
		description: z.string().nullish(),
	}).nullish(),
	invoiceTo: z.object({
		id: z.number(),
		name: z.string(),
	}).nullish(),
	invoiceGroupType: z.object({
		id: z.number(),
		name: z.string(),
		charterTypes: z.array(z.enum(CharterType)),
	}).nullish(),
	calculationMethod: ZodSchema.requiredField(CalculationMethodValidationSchema),
	conditions: z.array(AgreementConditionValidationSchema).nullish(),
	conditionsSummary: z.string().optional().transform(_ => undefined),
	isDefault: z.boolean().optional().transform(_ => undefined),
}).superRefine((row, ctx) => {
	const requiredFields = getRequiredAgreementFields(
		{
			agreementType: row.type ?? undefined,
			calculationMethodType: row.calculationMethod?.type ?? null,
			agreement: row,
		},
	);

	requiredFields.forEach((x) => {
		if (x.path == null) {
			return;
		}

		const path = x.path;

		let key, value;
		if (isTopLevelPath(path)) {
			key = path as keyof AgreementRow;
			value = row[key];
		} else if (isCalculationMethodPath(path)) {
			key = getKeyFromPath(PathType.CalculationMethod, path) as keyof CalculationMethodRow;
			value = row.calculationMethod?.[key];
		} else {
			return;
		}

		const isEmpty = (
			value == null
			|| (typeof value === "string" && value.trim().length === 0)
		);

		if (isEmpty) {
			throwError([ `${path}` ], ctx);
			return;
		}

	});
});
